import { showErrorAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { shouldSuppressErrorAlert } from '@/libs/errors/request-timeout';
import { useCoachQuery } from '@/features/home/queries';
import { homeKeys } from '@/features/home/queries/query-keys';
import { useListPlayersSessionQuery } from '@/features/player-session/queries';
import { playerSessionKeys } from '@/features/player-session/queries/query-keys';
import { useOverviewSessionUiStore } from '@/features/player-session/stores/overview-session-ui-store';
import { useSessionSelectPlayerStore } from '@/features/player-session/stores/session-select-player-store';
import { useListPlayer } from '@/features/player/hooks/use-list-player';
import {
  coachDocRef,
  getSession,
  sessionDocRef,
  updateDocMerge,
} from '@/libs/firebase/firestore/firestore-helpers';
import { useSessionStore } from '@/stores';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import uuid from 'react-native-uuid';

type SessionDoc = {
  academy_id?: string | null;
  coach_id?: string;
  active_coach_ids?: string[];
  roster?: string[];
  updated_at?: number;
  created_at?: number;
  /**
   * Redundant with Firestore document id, but kept for parity with iOS models/backends
   * that expect an `id` field inside the document data.
   */
  id?: string;
};

type CoachDoc = {
  academy_id?: string | null;
  active_session_id?: string | null;
};

const toUuidString = () => {
  const id = uuid.v4();
  return typeof id === 'string' ? id : String(id);
};

export function useSelectPlayer(options?: { limit?: number }) {
  const { limit = 50 } = options ?? {};
  const queryClient = useQueryClient();
  const userUid = useSessionStore(s => s.user?.uid);
  const { data: coach } = useCoachQuery(userUid);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedIds = useSessionSelectPlayerStore(s => s.selectedIds);
  const setSelectedIdsStore = useSessionSelectPlayerStore(s => s.setSelectedIds);
  const toggleSelectedIdStore = useSessionSelectPlayerStore(s => s.toggleSelectedId);
  const resetSelectionStore = useSessionSelectPlayerStore(s => s.reset);
  const requestOverviewSessionReset = useOverviewSessionUiStore(s => s.requestReset);
  const [sessionRosterReady, setSessionRosterReady] = useState(true);
  const hydratedRosterSignatureRef = useRef<string | null>(null);
  const sessionRosterErrorAlertedRef = useRef<string | null>(null);

  const activeSessionId = useMemo(() => {
    const id = coach?.active_session_id?.trim() ?? '';
    return id.length > 0 ? id : null;
  }, [coach?.active_session_id]);

  const {
    data: sessionRosterPage,
    isSuccess: isSessionRosterSuccess,
    isError: isSessionRosterError,
    error: sessionRosterError,
    isFetching: isSessionRosterFetching,
    isPlaceholderData: isSessionRosterPlaceholder,
  } = useListPlayersSessionQuery({
    sessionId: activeSessionId,
    sort: 'nameAsc',
  });

  const baseListPlayerLogic = useListPlayer({ limit });
  const onRefresh = useCallback(() => {
    if (activeSessionId) {
      // Pull-to-refresh should also refetch the session roster so `selectedIds` stays in sync.
      queryClient.invalidateQueries({
        queryKey: [...playerSessionKeys.all, playerSessionKeys.sessionList, activeSessionId],
      });
    }
    baseListPlayerLogic.refetch();
  }, [baseListPlayerLogic, queryClient, activeSessionId]);

  const listPlayerLogic = useMemo(
    () => ({
      ...baseListPlayerLogic,
      isLoading: isSessionRosterFetching || baseListPlayerLogic.isLoading || isSubmitting,
      refetch: onRefresh,
    }),
    [baseListPlayerLogic, isSessionRosterFetching, isSubmitting, onRefresh],
  );

  const selectedCount = selectedIds.size;
  const titleResultsSummary = useMemo(() => `${selectedCount} PLAYER(S) SELECTED`, [selectedCount]);

  const hasActiveSession = useMemo(() => activeSessionId !== null, [activeSessionId]);

  useEffect(() => {
    if (!activeSessionId) {
      hydratedRosterSignatureRef.current = null;
      sessionRosterErrorAlertedRef.current = null;
      setSessionRosterReady(true);
      resetSelectionStore();
      return;
    }
    hydratedRosterSignatureRef.current = null;
    sessionRosterErrorAlertedRef.current = null;
    setSessionRosterReady(false);
  }, [activeSessionId, resetSelectionStore]);

  useEffect(() => {
    if (!activeSessionId || !isSessionRosterSuccess || sessionRosterPage === undefined) return;
    // Avoid re-applying stale roster while TanStack shows placeholderData during refetch (e.g. after player delete).
    if (isSessionRosterPlaceholder) return;

    const rows = sessionRosterPage.data ?? [];
    const ids = rows.map(r => r.id);
    const signature = `${activeSessionId}:${ids.join('|')}`;
    if (hydratedRosterSignatureRef.current === signature) return;

    setSelectedIdsStore(ids);
    hydratedRosterSignatureRef.current = signature;
    setSessionRosterReady(true);
  }, [
    activeSessionId,
    isSessionRosterPlaceholder,
    isSessionRosterSuccess,
    sessionRosterPage,
    setSelectedIdsStore,
  ]);

  useEffect(() => {
    if (!activeSessionId || !isSessionRosterError) return;
    if (shouldSuppressErrorAlert(sessionRosterError)) {
      setSessionRosterReady(true);
      return;
    }
    if (sessionRosterErrorAlertedRef.current === activeSessionId) return;
    sessionRosterErrorAlertedRef.current = activeSessionId;
    showErrorAlert(STRINGS.errors.firestoreTimeout);
    setSessionRosterReady(true);
  }, [activeSessionId, isSessionRosterError, sessionRosterError]);

  const onToggleChecked = useCallback(
    (playerId: string) => {
      toggleSelectedIdStore(playerId);
    },
    [toggleSelectedIdStore],
  );

  const startContinue = useCallback(async () => {
    if (!userUid) {
      showErrorAlert(STRINGS.errors.firestoreTimeout);
      return null;
    }

    const roster = Array.from(selectedIds).filter(Boolean);
    if (roster.length === 0) {
      return null;
    }

    const existingSessionId = coach?.active_session_id?.trim();
    const targetSessionId = existingSessionId ? existingSessionId : toUuidString();
    const now = Date.now();

    setIsSubmitting(true);
    try {
      const existingSession = existingSessionId
        ? await getSession<SessionDoc>(existingSessionId)
        : null;
      const activeCoachIds =
        existingSession?.active_coach_ids && existingSession.active_coach_ids.length > 0
          ? existingSession.active_coach_ids
          : [userUid];

      await updateDocMerge<SessionDoc>(sessionDocRef<SessionDoc>(targetSessionId), {
        academy_id: coach?.academy_id ?? null,
        coach_id: userUid,
        active_coach_ids: activeCoachIds,
        roster,
        updated_at: now,
        ...(existingSessionId ? {} : { created_at: now }),
        id: targetSessionId,
      });

      await updateDocMerge<CoachDoc>(coachDocRef<CoachDoc>(userUid), {
        active_session_id: targetSessionId,
      });

      await queryClient.invalidateQueries({ queryKey: [...playerSessionKeys.all, 'list'] });
      if (!existingSessionId) {
        await queryClient.invalidateQueries({ queryKey: homeKeys.coach(userUid) });
      }

      requestOverviewSessionReset();
      return targetSessionId;
    } catch {
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    coach?.academy_id,
    coach?.active_session_id,
    queryClient,
    requestOverviewSessionReset,
    selectedIds,
    userUid,
  ]);

  const isRightActionDisabled =
    selectedCount === 0 || isSubmitting || (activeSessionId !== null && !sessionRosterReady);

  return {
    userUid,
    coach,
    hasActiveSession,
    isSubmitting,
    isSessionRosterFetching,
    sessionRosterReady,
    selectedIds,
    selectedCount,
    titleResultsSummary,
    isRightActionDisabled,
    onToggleChecked,
    startContinue,
    listPlayerLogic,
  };
}
